import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn, ZoomOut, Download, Maximize, Minimize, AlertTriangle, Info, Calendar, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const AlertViewerModal = ({ alert: rawAlert, isOpen, onClose }) => {
  const [scale, setScale] = useState(1);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  // ✅ Normalization كامل مع fallback لكل الحالات
  const normalizedAlert = React.useMemo(() => {
    if (!rawAlert) return {};
    
    console.log('🔍 Raw alert data:', rawAlert); // تشخيص
    
    return {
      // صورة - جرب كل الاحتمالات
      image: rawAlert.file_url || 
             (rawAlert.file_name ? `https://new.sbdemolition.online/uploads/alerts/${rawAlert.file_name}` : null) ||
             rawAlert.image ||
             null,
      
      // عنوان مع fallback
      title: rawAlert.title || rawAlert.Title || 'System Alert',
      
      // وصف
      description: rawAlert.message || rawAlert.description || 'No description available.',
      
      // أولوية
      priority: (rawAlert.priority || 'low').charAt(0).toUpperCase() + (rawAlert.priority || 'low').slice(1),
      
      // اسم المرسل
      uploadedBy: rawAlert.created_by_name || rawAlert.uploadedBy || 'System',
      
      // تاريخ
      createdAt: rawAlert.created_at || rawAlert.createdAt || new Date().toISOString(),
      
      // مشاهدات
      views: rawAlert.views || 0
    };
  }, [rawAlert]);

  console.log('✅ Normalized alert:', normalizedAlert); // تشخيص نهائي

  if (!isOpen || !normalizedAlert.title) return null;

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));
  
  const handleDownload = async () => {
    if (!normalizedAlert.image || imageError) {
      console.error('No image to download');
      return;
    }
    
    try {
      console.log('⬇️ Downloading:', normalizedAlert.image);
      const response = await fetch(normalizedAlert.image, { 
        method: 'GET',
        mode: 'cors',
        cache: 'default'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `alert-${normalizedAlert.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${Date.now()}.${blob.type.split('/')[1] || 'png'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      console.log('✅ Download success');
    } catch (error) {
      console.error('❌ Download failed:', error);
      
      // Fallback direct download
      const link = document.createElement('a');
      link.href = normalizedAlert.image;
      link.download = `alert-${Date.now()}.png`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const toggleFullScreen = async () => {
    const elem = document.getElementById('alert-modal-content');
    if (!document.fullscreenElement) {
      try {
        await elem?.requestFullscreen();
        setIsFullScreen(true);
      } catch (err) {
        console.error('Fullscreen failed:', err);
      }
    } else {
      try {
        await document.exitFullscreen();
        setIsFullScreen(false);
      } catch (err) {
        console.error('Exit fullscreen failed:', err);
      }
    }
  };

  const getPriorityColor = (priority) => {
    const p = priority?.toLowerCase() || 'low';
    switch(p) {
      case 'high':
      case 'critical': return 'bg-red-500 text-white border-red-600 shadow-md shadow-red-200/50';
      case 'medium': return 'bg-amber-500 text-white border-amber-600 shadow-md shadow-amber-200/50';
      case 'low': return 'bg-emerald-500 text-white border-emerald-600 shadow-md shadow-emerald-200/50';
      default: return 'bg-slate-500 text-white border-slate-600 shadow-md shadow-slate-200/50';
    }
  };

  const handleImageError = () => {
    console.error('🖼️ Image failed to load:', normalizedAlert.image);
    setImageError(true);
    setImageLoading(false);
  };

  const handleImageLoad = () => {
    console.log('🖼️ Image loaded successfully');
    setImageError(false);
    setImageLoading(false);
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-8"
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div 
            id="alert-modal-content"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className={cn(
              "bg-white rounded-2xl overflow-hidden shadow-2xl max-w-7xl w-full max-h-[95vh] flex flex-col lg:flex-row relative",
              isFullScreen && "h-screen w-screen max-w-none rounded-none !fixed inset-0 z-[10000]"
            )}
          >
            {/* Close Button */}
            <button 
              onClick={onClose}
              className="absolute top-6 right-6 z-50 p-3 bg-black/70 hover:bg-red-600 text-white rounded-2xl backdrop-blur-sm transition-all shadow-2xl hover:shadow-red-500/25 group"
              aria-label="Close alert modal"
              title="Close (Esc)"
            >
              <X className="w-6 h-6 group-hover:scale-110 transition-transform" />
            </button>

            {/* Left: Image */}
            <div className="flex-1 min-h-[400px] lg:min-h-0 relative overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900">
              {/* Fullscreen indicator */}
              {isFullScreen && (
                <div className="absolute top-6 left-6 z-40 bg-white/90 backdrop-blur-sm text-slate-900 px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                  <Minimize className="w-4 h-4 inline mr-1" />
                  Press ESC to exit fullscreen
                </div>
              )}

              {/* Toolbar */}
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 bg-black/85 backdrop-blur-xl p-4 rounded-3xl shadow-2xl border border-white/20">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-12 w-12 text-white hover:bg-white/20 p-0 rounded-2xl shadow-lg hover:shadow-white/20 transition-all" 
                  onClick={handleZoomOut}
                  disabled={scale <= 0.5}
                >
                  <ZoomOut className="w-6 h-6" />
                </Button>
                
                <div className="min-w-[72px] text-center">
                  <div className="text-2xl font-mono font-bold text-white">{Math.round(scale * 100)}%</div>
                  <div className="text-xs text-white/70 font-mono tracking-wider">zoom</div>
                </div>
                
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-12 w-12 text-white hover:bg-white/20 p-0 rounded-2xl shadow-lg hover:shadow-white/20 transition-all" 
                  onClick={handleZoomIn}
                  disabled={scale >= 3}
                >
                  <ZoomIn className="w-6 h-6" />
                </Button>

                <div className="w-px h-8 bg-white/30 mx-4" />

                <Button 
                  variant="ghost" 
                  size="icon" 
                  className={cn(
                    "h-12 w-12 text-white hover:bg-white/20 p-0 rounded-2xl shadow-lg hover:shadow-white/20 transition-all",
                    (!normalizedAlert.image || imageError) && "opacity-50 cursor-not-allowed"
                  )} 
                  onClick={handleDownload}
                  disabled={!normalizedAlert.image || imageError}
                  title="Download image"
                >
                  <Download className="w-6 h-6" />
                </Button>

                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-12 w-12 text-white hover:bg-white/20 p-0 rounded-2xl shadow-lg hover:shadow-white/20 transition-all" 
                  onClick={toggleFullScreen}
                  title="Toggle fullscreen"
                >
                  {isFullScreen ? <Minimize className="w-6 h-6" /> : <Maximize className="w-6 h-6" />}
                </Button>
              </div>

              {/* Image */}
              <motion.div
                drag={!!normalizedAlert.image && !imageError}
                dragConstraints={{ left: -1000, right: 1000, top: -1000, bottom: 1000 }}
                style={{ scale }}
                className="absolute inset-0 flex items-center justify-center p-12 cursor-grab active:cursor-grabbing"
              >
                {normalizedAlert.image && !imageError ? (
                  <img 
                    src={normalizedAlert.image} 
                    alt={normalizedAlert.title} 
                    className="max-w-full max-h-full object-contain shadow-2xl rounded-2xl drop-shadow-2xl" 
                    draggable={false}
                    loading="eager"
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                  />
                ) : (
                  <motion.div 
                    initial={{ opacity: 0.5 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center justify-center text-center p-16 bg-white/10 backdrop-blur-xl rounded-2xl border-4 border-dashed border-white/40 shadow-2xl max-w-md mx-auto"
                  >
                    <AlertTriangle className="w-20 h-20 text-yellow-400 mb-6 opacity-80 drop-shadow-lg" />
                    <h3 className="text-2xl font-bold text-white mb-3 drop-shadow-lg">
                      {imageError ? 'Image Unavailable' : 'No Image'}
                    </h3>
                    <p className="text-white/80 text-lg mb-6 max-w-sm leading-relaxed drop-shadow-md">
                      {imageError 
                        ? 'The image could not be loaded. Try refreshing.' 
                        : 'This alert does not have an image attachment.'
                      }
                    </p>
                    {imageError && (
                      <Button 
                        variant="outline" 
                        size="lg"
                        className="border-white/50 text-white hover:bg-white/20 backdrop-blur-sm"
                        onClick={() => {
                          setImageError(false);
                          setImageLoading(true);
                        }}
                      >
                        🔄 Retry
                      </Button>
                    )}
                  </motion.div>
                )}
              </motion.div>
            </div>

            {/* Right: Details */}
            <div className="w-full lg:w-96 bg-gradient-to-b from-white/90 via-slate-50 to-slate-100/80 backdrop-blur-xl p-10 flex flex-col border-l-4 border-gradient-to-b from-slate-200/50 shadow-2xl overflow-y-auto shrink-0">
              {/* Header */}
              <div className="mb-10">
                <div className="flex items-center justify-between mb-6 pb-6 border-b border-slate-200">
                  <Badge 
                    className={cn(
                      "px-4 py-2 text-base font-bold shadow-lg",
                      getPriorityColor(normalizedAlert.priority)
                    )}
                  >
                    {normalizedAlert.priority} Priority
                  </Badge>
                  <span className="text-sm text-slate-500 font-mono bg-slate-100 px-3 py-1 rounded-full">
                    {new Date(normalizedAlert.createdAt).toLocaleString('en-US', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                
                <h1 className="text-4xl lg:text-3xl font-black text-slate-900 leading-tight mb-8 bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent drop-shadow-lg">
                  {normalizedAlert.title}
                </h1>

                {/* Meta */}
                <div className="grid grid-cols-1 gap-4 bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-slate-200/50 shadow-xl">
                  <div className="flex items-center gap-4 text-slate-700">
                    <div className="w-10 h-10 bg-gradient-to-br from-slate-500 to-slate-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="font-bold text-lg text-slate-900">{normalizedAlert.uploadedBy}</div>
                      <div className="text-sm text-slate-500">System Administrator</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 text-slate-700 pt-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div className="font-mono text-sm text-slate-600">
                      Posted {new Date(normalizedAlert.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="flex-1 space-y-6">
                <div className="flex items-center gap-3 mb-6 pt-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Info className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 tracking-tight">
                    Alert Description
                  </h3>
                </div>
                
                <div className="prose prose-lg max-w-none bg-white/70 backdrop-blur-sm rounded-2xl p-8 border border-slate-200 shadow-2xl prose-headings:text-slate-900 prose-p:text-slate-700 leading-relaxed">
                  <p className="text-xl whitespace-pre-wrap">{normalizedAlert.description}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AlertViewerModal;
